import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Star, Award, CheckCircle2, Heart, Menu, X, User, MessageCircle, PenTool, Compass, Home, Laptop, Shield } from 'lucide-react';
import emailjs, { EMAIL_CONFIG } from './emailConfig';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: 'בירורים לגבי טיפול',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'שם מלא הוא שדה חובה';
        if (value.trim().length < 2) return 'שם חייב להכיל לפחות 2 תווים';
        return '';
      case 'phone':
        if (!value.trim()) return 'מספר טלפון הוא שדה חובה';
        const phoneRegex = /^0[2-9]\d{1,2}-?\d{7}$|^05[0-9]-?\d{7}$/;
        if (!phoneRegex.test(value.replace(/[-\s]/g, ''))) return 'מספר טלפון לא תקין';
        return '';
      case 'email':
        if (!value.trim()) return 'כתובת אימייל היא שדה חובה';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'כתובת אימייל לא תקינה';
        return '';
      case 'message':
        if (!value.trim()) return 'הודעה היא שדה חובה';
        if (value.trim().length < 10) return 'ההודעה חייבת להכיל לפחות 10 תווים';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const requiredFields = ['name', 'phone', 'email', 'message'];
    const errors: {[key: string]: string} = {};
    let hasErrors = false;

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field as keyof typeof formData]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    setFieldErrors(errors);
    setTouchedFields({
      name: true,
      phone: true,
      email: true,
      message: true
    });

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        EMAIL_CONFIG.TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          to_email: 'tabachnik.dana@gmail.com'
        }
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: 'בירורים לגבי טיפול',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-assistant" dir="rtl">
      {/* Skip Links for Screen Readers */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 bg-primary-600 text-white px-4 py-2 rounded-lg z-50">
        דלג לתוכן הראשי
      </a>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50" role="banner">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">דנה גליקמן</h1>
              <p className="text-sm text-gray-600">קלינאית תקשורת</p>
            </div>
            
            {/* Desktop Navigation - Centered */}
            <nav className="hidden md:flex space-x-8 space-x-reverse items-center absolute left-1/2 transform -translate-x-1/2" role="navigation" aria-label="תפריט ניווט ראשי">
              <a href="#home" className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-xl" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>אודות</a>
              <a href="#services" className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-xl">תחומי טיפול</a>
              <a href="#benefits" className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-xl">טיפול אונליין</a>

              <a href="#contact" className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-xl">צור קשר</a>
            </nav>
            
            {/* Spacer for balance */}

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "סגור תפריט" : "פתח תפריט"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-gray-200" id="mobile-menu" role="navigation" aria-label="תפריט ניווט נייד">
              <div className="flex flex-col space-y-4 pt-4">
                <a href="#home" className="text-gray-700 hover:text-primary-600 transition-colors font-medium" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setIsMenuOpen(false); }}>אודות</a>
                <a href="#services" className="text-gray-700 hover:text-primary-600 transition-colors font-medium" onClick={() => setIsMenuOpen(false)}>תחומי טיפול</a>
                <a href="#benefits" className="text-gray-700 hover:text-primary-600 transition-colors font-medium" onClick={() => setIsMenuOpen(false)}>טיפול אונליין</a>

                <a href="#contact" className="text-gray-700 hover:text-primary-600 transition-colors font-medium" onClick={() => setIsMenuOpen(false)}>צור קשר</a>
                
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main>
        <section id="main-content" className="bg-gradient-to-l from-primary-50 to-secondary-50 py-20" role="main" aria-labelledby="hero-heading">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 id="hero-heading" className="text-5xl md:text-6xl font-bold leading-tight mb-6">
                <span className="text-purple-500">דנה גליקמן</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-semibold">
                נעים מאוד, אני דנה גליקמן קלינאית תקשורת מוסמכת עם רישיון משרד&nbsp;הבריאות. נשואה לעומר ואמא של&nbsp;שון.<br />
                בוגרת תואר ראשון בהפרעות בתקשורת בהצטיינות מאוניברסיטת תל&nbsp;אביב.<br />
                בעלת תעודת טיפול קוגנטיבי התנהגותי ממכללת וינגייט ותעודת העברת קבוצות בבליותרפיסטיות מטעם המכון הישראלי&nbsp;לבבליותרפיה.<br />
                מקבלת מטופלים&nbsp;באונליין.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="bg-purple-500 text-white px-8 py-4 rounded-full hover:bg-purple-600 transition-all duration-300 font-medium text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover-lift inline-block text-center">
                  שלחו הודעה
                </a>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="w-full h-96 rounded-3xl overflow-hidden">
                  <img 
                    src="/dana-photo.png" 
                    alt="דנה גליקמן יושבת במשרדה המקצועי, מחייכת ועובדת על מחשב נייד. קלינאית תקשורת מוסמכת עם ניסיון של 5 שנים" 
                    className="w-full h-full object-cover"
                    style={{objectPosition: 'center 70%'}}
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center shadow-xl animate-float" aria-hidden="true">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary-500 rounded-full flex items-center justify-center shadow-xl animate-float" style={{animationDelay: '1s'}} aria-hidden="true">
                  <Star className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* About Section */}
      <section id="about" className="py-10 bg-gradient-to-br from-blue-50 to-white scroll-mt-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-3xl ">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">האני מאמין שלי</h3>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                  <Compass className="w-10 h-10 text-white" />
                </div>
              </div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed text-right flex-1 text-center md:text-right">
                אני מאמינה שעבודה משותפת של המטפל יחד עם המטופל באווירה נעימה, אמפתית ומכילה, מניבה את התוצאות הטובות ביותר. במהלך הטיפול, אני שמה דגש על הקניית מיומניות וכלים, לצד התייחסות לחוויות ולתחושות הפנימיות. מטרת העל מבחינתי היא שיפור תחושת השלומות, הwellbeing של האדם.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-10 bg-gradient-to-l from-primary-50 to-secondary-50 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">תחומי טיפול</h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-xl border-2 border-purple-100 hover:transition-all duration-300 hover:-translate-y-2 hover-lift flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <User className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">טיפול במתבגרים ובוגרים על רצף האוטיזם בתפקוד גבוה</h4>
              <p className="text-gray-600 leading-relaxed">
                התאמת הטיפול והמטרות בהתאם לרצונות האישיים של המטופל. התמקדות בתחום התקשורת.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-xl border-2 border-purple-100 hover:transition-all duration-300 hover:-translate-y-2 hover-lift flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">טיפול בגמגום בחברת נובוטוק</h4>
              <p className="text-gray-600 leading-relaxed mt-8">
                טיפול בגמגום דרך טכנולוגיה ייחודית המאפשרת תרגול בתוכנת מחשב לצד שיחות עם קלינאית. תרגול טכניקות דיבור ועידוד ליצירת חוויות דיבור טובה יותר.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-xl border-2 border-purple-100 hover:transition-all duration-300 hover:-translate-y-2 hover-lift flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <PenTool className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">העברת קבוצות כתיבה יעודיות לנשים אחרי לידה</h4>
              <p className="text-gray-600 leading-relaxed">
                קבוצות כתיבה המאפשרות לעבד ולהעלות לדף תחושות סביב הכניסה לאימהות. מרחב המאפשר שיתוף ותחושת שייכות.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="benefits" className="py-10 bg-gradient-to-br from-blue-50 to-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">יתרונות של טיפולי אונליין</h3>
          </div>
          
          <div className="grid md:grid-cols-6 gap-6">
            <div className="bg-white md:col-span-2 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">הגעה לטיפול מכל מקום</h4>
            </div>

            <div className="bg-white md:col-span-2 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-green-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">חסכון בזמן ומשאבים</h4>
            </div>

            <div className="bg-white md:col-span-2 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                <Home className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">התפנות לטיפול במקום בו אני מרגיש בנוח</h4>
            </div>

            <div className="bg-white md:col-span-2 md:col-start-2 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">שמירה על רצף טיפולי בתקופות מאתגרות</h4>
            </div>

            <div className="bg-white md:col-span-2 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-cyan-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                <Laptop className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">שימוש בטכנולוגיה, כתיבה משותפת צפייה בסרטונים ושיתוף מצגות</h4>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-10 bg-gradient-to-l from-primary-50 to-secondary-50 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">בואו נתחיל</h3>
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-lg">
              <h4 className="text-2xl font-bold text-gray-900 mb-6">שלחו הודעה</h4>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" role="alert" aria-live="polite">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 ml-2" aria-hidden="true" />
                    <span>ההודעה נשלחה בהצלחה! דנה תחזור אליכם בהקדם.</span>
                  </div>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" role="alert" aria-live="assertive">
                  <span>שגיאה בשליחת ההודעה. אנא נסו שוב או צרו קשר בטלפון.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" role="form" aria-label="טופס יצירת קשר">
                <div>
                  <label htmlFor="name" className={`block text-sm font-medium mb-2 transition-colors ${
                    touchedFields.name && fieldErrors.name 
                      ? 'text-red-600' 
                      : touchedFields.name && !fieldErrors.name 
                        ? 'text-green-600' 
                        : 'text-gray-700'
                  }`}>
                    שם מלא *
                  </label>
                  <div className="relative">
                    <input 
                      type="text" 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      aria-describedby={touchedFields.name && fieldErrors.name ? "name-error" : undefined}
                      aria-invalid={touchedFields.name && fieldErrors.name ? "true" : "false"}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 transition-colors focus-ring ${
                        touchedFields.name && fieldErrors.name
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : touchedFields.name && !fieldErrors.name
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                      }`}
                    />
                    {touchedFields.name && fieldErrors.name && (
                      <div id="name-error" className="absolute top-full left-0 mt-1 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10" role="alert" aria-live="polite">
                        {fieldErrors.name}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="phone" className={`block text-sm font-medium mb-2 transition-colors ${
                    touchedFields.phone && fieldErrors.phone 
                      ? 'text-red-600' 
                      : touchedFields.phone && !fieldErrors.phone 
                        ? 'text-green-600' 
                        : 'text-gray-700'
                  }`}>
                    טלפון *
                  </label>
                  <div className="relative">
                    <input 
                      type="tel" 
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      aria-describedby={touchedFields.phone && fieldErrors.phone ? "phone-error" : undefined}
                      aria-invalid={touchedFields.phone && fieldErrors.phone ? "true" : "false"}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 transition-colors focus-ring ${
                        touchedFields.phone && fieldErrors.phone
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : touchedFields.phone && !fieldErrors.phone
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                      }`}
                    />
                    {touchedFields.phone && fieldErrors.phone && (
                      <div id="phone-error" className="absolute top-full left-0 mt-1 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10" role="alert" aria-live="polite">
                        {fieldErrors.phone}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className={`block text-sm font-medium mb-2 transition-colors ${
                    touchedFields.email && fieldErrors.email 
                      ? 'text-red-600' 
                      : touchedFields.email && !fieldErrors.email 
                        ? 'text-green-600' 
                        : 'text-gray-700'
                  }`}>
                    אימייל *
                  </label>
                  <div className="relative">
                    <input 
                      type="email" 
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      aria-describedby={touchedFields.email && fieldErrors.email ? "email-error" : undefined}
                      aria-invalid={touchedFields.email && fieldErrors.email ? "true" : "false"}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 transition-colors focus-ring ${
                        touchedFields.email && fieldErrors.email
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : touchedFields.email && !fieldErrors.email
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                      }`}
                    />
                    {touchedFields.email && fieldErrors.email && (
                      <div id="email-error" className="absolute top-full left-0 mt-1 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10" role="alert" aria-live="polite">
                        {fieldErrors.email}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">נושא הפנייה</label>
                  <div className="relative">
                    <select 
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors focus-ring appearance-none bg-white"
                      style={{backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")', backgroundPosition: 'left 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em'}}
                    >
                      <option value="שאלות כלליות">שאלות כלליות</option>
                      <option value="בירורים לגבי טיפול">בירורים לגבי טיפול</option>
                      <option value="אחר">אחר</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className={`block text-sm font-medium mb-2 transition-colors ${
                    touchedFields.message && fieldErrors.message 
                      ? 'text-red-600' 
                      : touchedFields.message && !fieldErrors.message 
                        ? 'text-green-600' 
                        : 'text-gray-700'
                  }`}>
                    הודעה *
                  </label>
                  <div className="relative">
                    <textarea 
                      rows={4} 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      aria-describedby={touchedFields.message && fieldErrors.message ? "message-error" : undefined}
                      aria-invalid={touchedFields.message && fieldErrors.message ? "true" : "false"}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 transition-colors focus-ring ${
                        touchedFields.message && fieldErrors.message
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : touchedFields.message && !fieldErrors.message
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                      }`}
                    ></textarea>
                    {touchedFields.message && fieldErrors.message && (
                      <div id="message-error" className="absolute top-full left-0 mt-1 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10" role="alert" aria-live="polite">
                        {fieldErrors.message}
                      </div>
                    )}
                  </div>
                </div>
                
                <button 
                  type="submit"
                  disabled={isSubmitting || (Object.values(touchedFields).some(touched => touched) && (Object.values(fieldErrors).some(error => error !== '') || !formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()))}
                  className={`w-full py-4 rounded-lg font-medium text-lg transition-all duration-300 transform hover:-translate-y-1 hover-lift ${
                    isSubmitting || (Object.values(touchedFields).some(touched => touched) && (Object.values(fieldErrors).some(error => error !== '') || !formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()))
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-primary-600 to-secondary-500 text-white hover:shadow-lg'
                  }`}
                >
                  {isSubmitting ? 'שולח...' : 'שלח הודעה'}
                </button>
              </form>
            </div>
          </div>
      </section>

      </main>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/972525792787?text=היי%20דנה,%20אשמח%20לקבל%20פרטים%20נוספים%20על%20הטיפולים"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-white p-3 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 hover-lift group"
        aria-label="פתח שיחה בווטסאפ"
      >
        <svg 
          className="w-6 h-6" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" role="contentinfo">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center space-x-3 space-x-reverse mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-500 rounded-full flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h5 className="text-lg font-bold">דנה גליקמן</h5>
                  <p className="text-gray-400 text-sm">קלינאית תקשורת מוסמכת</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                בוגרת תואר ראשון בהפרעות בתקשורת בהצטיינות מאוניברסיטת תל אביב.
                בעלת תעודת טיפול קוגנטיבי התנהגותי ממכללת וינגייט ותעודת העברת קבוצות בבליותרפיסטיות מטעם המכון הישראלי לבבליותרפיה.
                מקבלת מטופלים באונליין.
              </p>
            </div>
            
            <div>
              <h5 className="text-lg font-bold mb-4">פרטי התקשרות</h5>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary-400" />
                  <p className="text-gray-400">052-579-2787</p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary-400" />
                  <p className="text-gray-400">tabachnik.dana@gmail.com</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 דנה גליקמן - קלינאית תקשורת. כל הזכויות שמורות.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 